import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import TrustWealthLogo from './TrustWealthLogo';

export default function Navigation() {
  const { currentUser, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = userRole === 'admin';
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const notificationsRef = useRef(null);
  const profileRef = useRef(null);

  // Close notifications when clicking outside
  useEffect(() => {
    if (!showNotifications) return;
    const handleClickOutside = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!isProfileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      type: 'success',
      message: 'A new login session started',
      time: 'Now'
    },
    {
      id: 2,
      type: 'info',
      message: 'New statement is available for viewing',
      time: '1 day ago'
    },
    {
      id: 3,
      type: 'warning',
      message: 'Please verify your email address',
      time: '2 days ago'
    }
  ];

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNavigation = (path, sectionId) => {
    if (window.location.pathname !== '/dashboard') {
      navigate('/dashboard');
      // Wait for navigation to complete before scrolling
      setTimeout(() => scrollToSection(sectionId), 100);
    } else {
      scrollToSection(sectionId);
    }
  };

  const memberNav = [
    { name: 'Dashboard', action: () => navigate('/dashboard'), icon: '🏠', path: '/dashboard' },
    { name: 'Transfers', action: () => handleNavigation('/dashboard', 'transfer-section'), icon: '↔️', path: null },
    { name: 'Cards', action: () => { navigate('/dashboard#quick-actions-cards'); setTimeout(() => scrollToSection('quick-actions'), 150); }, icon: '💳', path: null },
    { name: 'Bills', action: () => { navigate('/dashboard#quick-actions-billpay'); setTimeout(() => scrollToSection('quick-actions'), 150); }, icon: '💰', path: null },
  ];

  const adminNav = [
    { name: 'Admin Dashboard', action: () => navigate('/admin'), icon: '⚙️', path: '/admin' },
    { name: 'Reset Transfers', action: () => navigate('/admin/reset-transfers'), icon: '🔄', path: '/admin/reset-transfers' },
  ];

  const navigation = isAdmin ? adminNav : memberNav;

  return (
    <nav className={`fixed top-0 left-0 right-0 bg-white border-b border-gray-100 z-50 transition-all duration-200
      ${isScrolled ? 'shadow-md' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-24 md:h-28">
          {/* Logo and Navigation Links */}
          <div className="flex">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center px-3">
              <TrustWealthLogo />
            </div>

            {/* Navigation Links */}
            <div className="hidden md:ml-12 md:flex md:items-center md:space-x-1">
              {navigation.map((item) => {
                const isActive = item.path
                  ? (item.path === '/admin' ? location.pathname === '/admin' : location.pathname === item.path)
                  : false;
                const useAdminStyle = isAdmin;
                return (
                  <button
                    key={item.name}
                    onClick={item.action}
                    className={`px-3 py-2 rounded-lg flex items-center space-x-2 group transition-all duration-200
                      ${useAdminStyle
                        ? isActive
                          ? 'bg-amber-100 text-amber-800'
                          : 'text-amber-700 hover:text-amber-800 hover:bg-amber-50'
                        : isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform duration-200">{item.icon}</span>
                    <span className="font-medium">{item.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right side menu */}
          <div className="flex items-center space-x-4">
            {/* Help Button */}
            <div className="relative">
              <button
                className="p-2 text-gray-500 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                onMouseEnter={() => setShowHelpTooltip(true)}
                onMouseLeave={() => setShowHelpTooltip(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              {/* Help Tooltip - fixed on mobile so it stays in viewport */}
              {showHelpTooltip && (
                <div className="fixed left-4 right-4 top-24 z-50 w-auto md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:w-72 md:max-w-[calc(100vw-1.5rem)] bg-white rounded-xl shadow-lg border border-gray-100 p-4 transform transition-all duration-200">
                  <div className="flex items-center space-x-3 text-gray-600">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm">Need assistance? Contact us:</p>
                      <a 
                        href="mailto:support@willowvalecu.com"
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        support@willowvalecu.com
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications Button */}
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {/* Notification Badge */}
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* Notifications Popover - fixed on mobile so it stays in viewport */}
              {showNotifications && (
                <div className="fixed left-4 right-4 top-24 z-50 flex max-h-[min(70vh,24rem)] w-auto flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg transition-all duration-200 md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:w-80 md:max-w-[calc(100vw-1.5rem)]">
                  <div className="p-4 border-b border-gray-100 shrink-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                      <span className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
                        Mark all as read
                      </span>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-gray-100 min-h-0 flex-1 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div key={notification.id} className="p-4 hover:bg-gray-50 transition-colors duration-200">
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0
                            ${notification.type === 'success' ? 'bg-green-500' :
                              notification.type === 'warning' ? 'bg-yellow-500' :
                              'bg-primary-500'}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{notification.message}</p>
                            <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-gray-50 border-t border-gray-100 shrink-0">
                    
                  </div>
                </div>
              )}
            </div>
            
            {/* Profile Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 
                  hover:bg-primary-50 transition-all duration-200"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 
                  flex items-center justify-center text-white shadow-sm">
                  {currentUser?.email?.[0].toUpperCase() || 'U'}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{currentUser?.email?.split('@')[0]}</p>
                  <p className="text-xs text-gray-500">
                    {isAdmin ? (
                      <span className="inline-flex items-center text-amber-700 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5" /> Admin
                      </span>
                    ) : (
                      'Personal Account'
                    )}
                  </p>
                </div>
                <svg className="hidden md:block w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Profile Dropdown Menu - fixed on mobile so it stays in viewport */}
              {isProfileOpen && (
                <div className="fixed left-4 right-4 top-24 z-50 w-auto rounded-xl bg-white py-2 shadow-lg ring-1 ring-black ring-opacity-5 transition-all duration-200 md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:w-64 md:max-w-[calc(100vw-1.5rem)]">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{currentUser?.email}</p>
                    <p className="text-xs text-gray-500">
                      {isAdmin ? (
                        <span className="text-amber-600 font-medium">Admin</span>
                      ) : (
                        'Personal Banking'
                      )}
                    </p>
                  </div>
                  <div className="px-2 py-2 space-y-1">
                    {isAdmin && (
                      <>
                        <button
                          className="w-full flex items-center text-left px-2 py-2 text-sm text-amber-700 hover:bg-amber-50
                            rounded-lg transition-colors duration-200 font-medium"
                          onClick={() => { navigate('/admin'); setIsProfileOpen(false); }}
                        >
                          <svg className="w-5 h-5 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          Admin Dashboard
                        </button>
                        <button
                          className="w-full flex items-center text-left px-2 py-2 text-sm text-gray-700 hover:bg-primary-50
                            rounded-lg transition-colors duration-200"
                          onClick={() => { navigate('/admin/reset-transfers'); setIsProfileOpen(false); }}
                        >
                          <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Reset Transfer Counts
                        </button>
                      </>
                    )}
                    <button 
                      className="w-full flex items-center text-left px-2 py-2 text-sm text-red-600 hover:bg-red-50 
                        rounded-lg transition-colors duration-200" 
                      onClick={handleLogout}
                    >
                      <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-primary-50 
                  transition-all duration-200"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-3 border-t border-gray-100">
            <div className="space-y-1 px-2">
              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => { item.action(); setIsMobileMenuOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200
                    ${isAdmin ? 'text-amber-700 hover:bg-amber-50' : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'}`}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </button>
              ))}
              <div className="h-px bg-gray-100 my-2"></div>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-primary-600 
                hover:bg-primary-50 rounded-lg transition-all duration-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Help</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 px-3 py-2 text-red-600 hover:bg-red-50 
                  rounded-lg transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="font-medium">Sign out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}